import React from "react";
import Parse from "parse";
import { DEFAULT_TENANT_ID } from "../const";
import { useTranslation, AdminLayout } from "@opendash/core";
import { AdminToolbar } from "@opendash/ui";

import { Button, List, Space, Tag } from "antd";
import { useParseQuery } from "parse-hooks";
import { TenantConfig, Video } from "../types";
import { Icon } from "@opendash/icons";
import { ParseObjectOrderButtons } from "./ParseObjectOrderButtons";
import { $parse } from "@opendash/plugin-parse";

export const VideoAdminRoute = React.memo(function VideoAdminRoute() {
  const t = useTranslation();
  const [getVideoQuery, setGetVideoQuery] = React.useState<string[]>([]);
  const [update, setUpdate] = React.useState<boolean>(false);

  const currentTenant = Parse.User.current()?.get("tenant");

  React.useEffect(() => {
    async function fetchDate() {
      if (!currentTenant.get("tenantConfig").get("hideVideoIds")) {
        const tenantConfig = await new Parse.Query(TenantConfig)
          .equalTo("tenant", currentTenant)
          .first();
        setGetVideoQuery(tenantConfig?.get("hideVideoIds"));
      } else {
        setGetVideoQuery(currentTenant.get("tenantConfig").get("hideVideoIds"));
      }
    }
    fetchDate();
  }, []);

  let tenant = new Parse.Object("OD3_Tenant");
  tenant.id = DEFAULT_TENANT_ID;

  const query = React.useMemo(
    () =>
      DEFAULT_TENANT_ID == currentTenant
        ? new Parse.Query(Video)
            .equalTo("tenant", currentTenant)
            .descending("order")
            .limit(1000000)
        : Parse.Query.or(
            new Parse.Query(Video)
              .equalTo("tenant", tenant)
              .descending("order")
              .limit(1000000),
            new Parse.Query(Video)
              .equalTo("tenant", currentTenant)
              .descending("order")
              .limit(1000000)
          ),
    [currentTenant?.id]
  );

  const changeHideVideoIds = async (id: any, isHidden: boolean) => {
    const hideVideoIds = getVideoQuery || [];
    if (isHidden) {
      hideVideoIds.push(id);
    } else {
      hideVideoIds.splice(hideVideoIds.indexOf(id), 1);
    }
    currentTenant.get("tenantConfig").set("hideVideoIds", hideVideoIds);
    await currentTenant.get("tenantConfig").save();
    setGetVideoQuery(hideVideoIds);
    setUpdate(!update);
  };

  const entries = useParseQuery(query);

  return (
    <AdminLayout contentPadding>
      <AdminToolbar
        title={t("app:admin.video.title")}
        description={t("app:admin.video.description")}
        padding={false}
        actions={[
          <Button
            key="new"
            type="primary"
            icon={<Icon icon="fa:plus" />}
            children={t("app:admin.video.action_new")}
            onClick={() => {
              $parse.ui.createObject("APP_Video").then((objectId) => {
                entries.reload();
              });
            }}
          />,
        ]}
      />
      <div className="od-page-main">
        <List
          loading={entries.loading}
          dataSource={entries.result}
          renderItem={(entry) => {
            return (
              <List.Item>
                <>
                  <List.Item.Meta
                    title={
                      <span>
                        {entry.get("title")}{" "}
                        <Tag>{entry.get("language")?.id}</Tag>
                      </span>
                    }
                    description={entry.get("description")}
                  />
                  {entry.get("tenant").id == currentTenant.id ? (
                    <Space direction="horizontal">
                      {DEFAULT_TENANT_ID == currentTenant.id && (
                        <ParseObjectOrderButtons
                          objects={entries.result}
                          current={entry}
                          onChange={() => {
                            entries.reload();
                          }}
                        />
                      )}

                      <span style={{ display: "inline-block", width: 20 }} />

                      <Button
                        key="edit"
                        type="primary"
                        icon={<Icon icon="fa:edit" />}
                        children={t("app:admin.video.action_edit")}
                        onClick={() => {
                          $parse.ui
                            .updateObject(entry.className, entry.id)
                            .then((saved) => {
                              if (saved) {
                                entries.reload();
                              }
                            });
                        }}
                      />

                      <Button
                        key="delete"
                        type="default"
                        danger
                        icon={<Icon icon="fa:trash" />}
                        children={t("app:admin.video.action_delete")}
                        onClick={() => {
                          $parse.ui
                            .deleteObject(entry.className, entry.id)
                            .then((deleted) => {
                              if (deleted) {
                                entries.reload();
                              }
                            });
                        }}
                      />
                    </Space>
                  ) : (
                    <></>
                  )}
                  {DEFAULT_TENANT_ID != currentTenant.id &&
                  getVideoQuery?.includes(entry.id) ? (
                    <Button
                      danger
                      icon={<Icon icon="fa:eye" />}
                      children={t("app:admin.video.action_hidden")}
                      onClick={() => {
                        changeHideVideoIds(entry.id, false);
                      }}
                    />
                  ) : (
                    <Button
                      icon={<Icon icon="fa:eye" />}
                      children={t("app:admin.video.action_shown")}
                      onClick={() => {
                        changeHideVideoIds(entry.id, true);
                      }}
                    />
                  )}
                </>
              </List.Item>
            );
          }}
        />
      </div>
    </AdminLayout>
  );
});
